import { getDataStoreEntityMap } from './dataStoreEntityMap'
import { resolveInboxThreadDeviceId } from './gpsTrackerChatPresentation'
import {
  isDeviceRestStoreId,
  isPlatformChatDeviceId,
  resolveDeviceChatIds
} from './resolveDeviceChatIds'

const MASS_OR_MONITOR_TARGETS = new Set(['massmessage', 'monitor_message'])

/**
 * Devices map from the Redux root state (same shape Chat / ChatTab use).
 * @param {object} [state]
 * @returns {Record<string, object>}
 */
export function getDevicesByIdFromState (state) {
  return getDataStoreEntityMap(state?.data?.devices)
}

/**
 * Conversation device uuid from a Web Push / socket payload.
 * Prefer a real device uuid over operator/system labels (zone warnings).
 *
 * @param {object} [payload]
 * @returns {string | null}
 */
export function resolvePushChatDeviceUuid (payload = {}) {
  const sender =
    payload.deviceUuid ??
    payload.uuid_device_sender ??
    payload.chatDevice ??
    null
  const target =
    payload.uuid_device_target ??
    payload.monitor_message_uuid_device_target ??
    null

  if (sender != null && !isPlatformChatDeviceId(sender)) {
    return String(sender)
  }
  if (
    target != null &&
    !isPlatformChatDeviceId(target) &&
    !MASS_OR_MONITOR_TARGETS.has(String(target).toLowerCase())
  ) {
    return String(target)
  }
  return null
}

/**
 * Canonical id for `<Chat deviceId>` / `openMainChatForDevice`.
 * Prefers the REST store Guid (map marker shortcut) so GET /tracker/:id succeeds.
 * Falls back to a socket uuid (numeric GPS tracker ids) for message matching.
 *
 * @param {string | number | null | undefined} deviceIdOrUuid
 * @param {Record<string, object>} [devicesById]
 * @returns {string | null}
 */
export function resolveOpenChatDeviceId (deviceIdOrUuid, devicesById) {
  if (deviceIdOrUuid == null || deviceIdOrUuid === '' || isPlatformChatDeviceId(deviceIdOrUuid)) {
    return null
  }
  const { storeId, uuid } = resolveDeviceChatIds(devicesById || {}, deviceIdOrUuid)
  return storeId || uuid || String(deviceIdOrUuid)
}

/**
 * Open a 1:1 main-chat thread from an inbox / store message.
 * Device XOR user — never both (Chat prefers deviceUuid over userId when both are set).
 *
 * @param {object | null | undefined} message
 * @param {Record<string, object>} [devicesById]
 * @returns {{ deviceId: string | undefined, userId: string | undefined }}
 */
export function resolveOpenChatThreadFromMessage (message, devicesById) {
  const fromMessage = resolveInboxThreadDeviceId(message, devicesById || {})
  const deviceId = fromMessage
    ? (resolveOpenChatDeviceId(fromMessage, devicesById) || fromMessage)
    : null
  if (deviceId) {
    return { deviceId, userId: undefined }
  }
  const userId = message?.senderUserId ? String(message.senderUserId) : undefined
  return { deviceId: undefined, userId }
}

export { isDeviceRestStoreId, resolveInboxThreadDeviceId }
